import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import { RootState } from "../../store/index.ts";
import {
  fetchMyAdminNotifications,
  markAdminNotificationRead,
  markAllAdminNotificationsRead,
  seedAdminNotificationsDemo,
  AdminNotification,
} from "../../services/adminNotification.service.ts";
import {
  markAdminNotificationReadLocal,
  markAllAdminNotificationsReadLocal,
  setAdminNotificationsLoading,
  setAdminNotificationsPage,
  setAdminUnreadCount,
} from "../../store/slices/adminNotificationsSlice.ts";
import { notification } from "../../utils/Notifications.tsx";
import {
  formatFriendlyDate,
  getModuleIcon,
  getModuleLabel,
  getModuleTone,
} from "./notificationUi.ts";
import "./MyNotifications.css";

const PAGE_SIZE = 20;

type FilterKey = "all" | "unread" | "read";

const MyNotifications: React.FC = () => {
  useAppSettings();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, total, unreadCount, loading, offset, limit } = useSelector(
    (state: RootState) => state.adminNotifications
  );
  const [filter, setFilter] = React.useState<FilterKey>("all");

  const loadPage = React.useCallback(
    async (nextOffset = 0) => {
      dispatch(setAdminNotificationsLoading(true));
      try {
        const page = await fetchMyAdminNotifications(PAGE_SIZE, nextOffset);
        dispatch(
          setAdminNotificationsPage({
            items: page.items,
            total: page.total,
            unreadCount: page.unreadCount,
            offset: nextOffset,
            limit: PAGE_SIZE,
          })
        );
      } catch (error: any) {
        notification(
          "Error",
          error?.message || "No se pudieron cargar las notificaciones",
          "danger"
        );
      } finally {
        dispatch(setAdminNotificationsLoading(false));
      }
    },
    [dispatch]
  );

  React.useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  const handleMarkAll = async () => {
    try {
      await markAllAdminNotificationsRead();
      dispatch(markAllAdminNotificationsReadLocal());
      dispatch(setAdminUnreadCount(0));
      await loadPage(offset);
      notification(
        "Listo",
        "Todas las notificaciones fueron marcadas como leídas",
        "success"
      );
    } catch (error: any) {
      notification(
        "Error",
        error?.message || "No se pudo marcar como leídas",
        "danger"
      );
    }
  };

  const handleItemClick = async (item: AdminNotification) => {
    try {
      if (!item.isRead) {
        await markAdminNotificationRead(item.id);
        dispatch(markAdminNotificationReadLocal(item.id));
      }
    } catch {
      /* continue */
    }

    if (item.link) {
      navigate(item.link);
    } else {
      await loadPage(offset);
    }
  };

  const handleSeedDemo = async () => {
    try {
      const result = await seedAdminNotificationsDemo();
      notification(
        "Demo",
        result?.message || "Notificaciones de prueba creadas",
        "success"
      );
      await loadPage(0);
    } catch (error: any) {
      notification(
        "Error",
        error?.message || "Solo disponible en development",
        "danger"
      );
    }
  };

  const readCount = Math.max(0, total - unreadCount);
  const displayedItems = items.filter((item) => {
    if (filter === "unread") return !item.isRead;
    if (filter === "read") return item.isRead;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(total / (limit || PAGE_SIZE)));
  const currentPage = Math.floor(offset / (limit || PAGE_SIZE)) + 1;
  const showPagination = total > PAGE_SIZE;

  return (
    <Layout>
      <Title
        baseTitle="Mis notificaciones"
        basePath="/mis-notificaciones"
        title="Mis notificaciones"
        subtitle="Mantente al día con solicitudes y cambios importantes."
      />

      <div className="my-notif-page">
        <div className="my-notif-metrics" aria-label="Resumen de notificaciones">
          <div className="my-notif-metric">
            <span
              className="my-notif-metric__icon my-notif-metric__icon--unread"
              aria-hidden
            >
              <i className="fa-solid fa-bell" />
            </span>
            <span>
              <span className="my-notif-metric__value">{unreadCount}</span>
              <span className="my-notif-metric__label">No leídas</span>
            </span>
          </div>
          <div className="my-notif-metric">
            <span
              className="my-notif-metric__icon my-notif-metric__icon--read"
              aria-hidden
            >
              <i className="fa-solid fa-check" />
            </span>
            <span>
              <span className="my-notif-metric__value">{readCount}</span>
              <span className="my-notif-metric__label">Leídas</span>
            </span>
          </div>
          <div className="my-notif-metric">
            <span
              className="my-notif-metric__icon my-notif-metric__icon--total"
              aria-hidden
            >
              <i className="fa-solid fa-inbox" />
            </span>
            <span>
              <span className="my-notif-metric__value">{total}</span>
              <span className="my-notif-metric__label">Total</span>
            </span>
          </div>
        </div>

        <div className="my-notif-toolbar">
          <div
            className="my-notif-filters"
            role="tablist"
            aria-label="Filtrar notificaciones"
          >
            <button
              type="button"
              role="tab"
              aria-selected={filter === "all"}
              className={`my-notif-filter${filter === "all" ? " is-active" : ""}`}
              onClick={() => setFilter("all")}
            >
              Todas
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === "unread"}
              className={`my-notif-filter${
                filter === "unread" ? " is-active" : ""
              }`}
              onClick={() => setFilter("unread")}
            >
              No leídas
              {unreadCount > 0 && (
                <span className="my-notif-filter__count">{unreadCount}</span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === "read"}
              className={`my-notif-filter${
                filter === "read" ? " is-active" : ""
              }`}
              onClick={() => setFilter("read")}
            >
              Leídas
            </button>
          </div>

          <div className="my-notif-toolbar__actions">
            {process.env.NODE_ENV === "development" && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleSeedDemo}
                aria-label="Cargar notificaciones de demostración"
              >
                Cargar demo
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={unreadCount === 0 || loading}
              onClick={handleMarkAll}
              aria-label="Marcar todas las notificaciones como leídas"
            >
              Marcar todas como leídas
            </button>
          </div>
        </div>

        {loading && (
          <div className="my-notif-loading" role="status" aria-live="polite">
            <div className="my-notif-loading__icon" aria-hidden>
              <i className="fa-solid fa-spinner fa-spin" />
            </div>
            Cargando notificaciones...
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="my-notif-empty">
            <div className="my-notif-empty__icon" aria-hidden>
              <i className="fa-solid fa-bell" />
            </div>
            <p className="my-notif-empty__title">No tienes notificaciones</p>
            <p className="my-notif-empty__text">
              Cuando haya algo nuevo, aparecerá aquí.
            </p>
          </div>
        )}

        {!loading && items.length > 0 && displayedItems.length === 0 && (
          <div className="my-notif-empty">
            <div className="my-notif-empty__icon" aria-hidden>
              <i className="fa-solid fa-filter" />
            </div>
            <p className="my-notif-empty__title">
              No hay notificaciones en este filtro
            </p>
            <p className="my-notif-empty__text">
              Prueba con otra opción o cambia de página.
            </p>
          </div>
        )}

        {!loading && displayedItems.length > 0 && (
          <div className="my-notif-list">
            {displayedItems.map((item) => {
              const hasLink = Boolean(item.link);
              const tone = getModuleTone(item.module);
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`my-notif-card ${
                    item.isRead ? "my-notif-card--read" : "my-notif-card--unread"
                  }`}
                  onClick={() => handleItemClick(item)}
                  aria-label={`${item.title}. ${
                    item.isRead ? "Leída" : "No leída"
                  }. ${getModuleLabel(item.module)}`}
                >
                  {item.isRead ? (
                    <span className="my-notif-card__dot-spacer" aria-hidden />
                  ) : (
                    <span className="my-notif-card__dot" aria-hidden />
                  )}

                  <span
                    className={`my-notif-card__icon my-notif-card__icon--${tone}`}
                    aria-hidden
                  >
                    <i className={getModuleIcon(item.module)} />
                  </span>

                  <span className="my-notif-card__body">
                    <span className="my-notif-card__title">{item.title}</span>
                    <span className="my-notif-card__desc">{item.body}</span>
                    <span className={`my-notif-tag my-notif-tag--${tone}`}>
                      {getModuleLabel(item.module)}
                    </span>
                  </span>

                  <span className="my-notif-card__aside">
                    <span className="my-notif-card__date">
                      {formatFriendlyDate(item.createdAt)}
                    </span>
                    {hasLink ? (
                      <span className="my-notif-card__chevron" aria-hidden>
                        <i className="fa-solid fa-chevron-right" />
                      </span>
                    ) : (
                      <span
                        className="my-notif-card__chevron-spacer"
                        aria-hidden
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {showPagination && (
          <div className="my-notif-pagination">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={currentPage <= 1 || loading}
              onClick={() => loadPage(Math.max(0, offset - PAGE_SIZE))}
              aria-label="Página anterior"
            >
              <i className="fa-solid fa-chevron-left me-1" aria-hidden />
              Anterior
            </button>
            <span className="my-notif-pagination__label">
              Página {currentPage} de {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={currentPage >= totalPages || loading}
              onClick={() => loadPage(offset + PAGE_SIZE)}
              aria-label="Página siguiente"
            >
              Siguiente
              <i className="fa-solid fa-chevron-right ms-1" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyNotifications;
